<template>
  <div class="login-page">
    <AppSidebar />
    
    <div class="login-container">
      <div class="login-box">
        <div class="login-header">
          <h2>用户登录</h2>
          <p>欢迎回来，请登录您的账号</p>
        </div>
        
        <div class="login-form">
          <div class="form-group" :class="{ 'has-error': errors.account }">
            <label for="account">账号</label>
            <input 
              type="text" 
              id="account" 
              v-model="account" 
              placeholder="请输入用户名或手机号"
              @input="clearError('account')"
            />
            <div class="error-message" v-if="errors.account">{{ errors.account }}</div>
          </div>
          
          <div class="form-group" :class="{ 'has-error': errors.password }">
            <label for="password">密码</label>
            <input 
              type="password" 
              id="password" 
              v-model="password" 
              placeholder="请输入密码"
              @input="clearError('password')"
            />
            <div class="error-message" v-if="errors.password">{{ errors.password }}</div>
          </div>
          
          <div class="form-options">
            <div class="remember-me">
              <input type="checkbox" id="remember" v-model="remember" />
              <label for="remember">记住我</label>
            </div>
            <a href="#" class="forgot-password">忘记密码?</a>
          </div>
          
          <button 
            class="login-button" 
            @click="handleLogin" 
            :disabled="loading"
          >
            <span v-if="!loading">登录</span>
            <span v-else class="loading-spinner"></span>
          </button>
          
          <div class="login-footer">
            <p>还没有账号? <router-link to="/register" class="register-link">立即注册</router-link></p>
          </div>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { login } from '@/api/user';
import AppSidebar from '@/components/layout/Sidebar.vue';
import AppFooter from '@/components/layout/Footer.vue';

export default {
  name: 'LoginPage',
  components: {
    AppSidebar,
    AppFooter
  },
  setup() {
    const router = useRouter();
    const account = ref('');
    const password = ref('');
    const remember = ref(false);
    const loading = ref(false);
    const errors = ref({
      account: '',
      password: ''
    });

    const validateForm = () => {
      let isValid = true;
      errors.value = {
        account: '',
        password: ''
      };

      if (!account.value.trim()) {
        errors.value.account = '请输入账号';
        isValid = false;
      }

      if (!password.value.trim()) {
        errors.value.password = '请输入密码';
        isValid = false;
      } else if (password.value.length < 6) {
        errors.value.password = '密码长度不能少于6位';
        isValid = false;
      }

      return isValid;
    };

    const clearError = (field) => {
      errors.value[field] = '';
    };

    const handleLogin = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      
      try {
        await login(account.value, password.value);
        
        // 获取重定向地址
        const redirect = router.currentRoute.value.query.redirect || '/user/center';
        // 登录成功，优先重定向到查询参数中的redirect，如果没有则重定向到用户中心
        router.push(redirect);
      } catch (error) {
        errors.value.account = error.message || '登录失败，请检查账号和密码';
      } finally {
        loading.value = false;
      }
    };

    return {
      account,
      password,
      remember,
      loading,
      errors,
      clearError,
      handleLogin
    };
  }
};
</script>

<style scoped>
.login-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.login-container {
  margin-left: 200px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  background-color: #f5f5f5;
}

.login-box {
  width: 100%;
  max-width: 450px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h2 {
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
}

.login-header p {
  color: #666;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-group input:focus {
  border-color: #ff5a5f;
  outline: none;
}

.form-group.has-error input {
  border-color: #ff5a5f;
}

.error-message {
  color: #ff5a5f;
  font-size: 12px;
  margin-top: 5px;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.remember-me {
  display: flex;
  align-items: center;
}

.remember-me input {
  margin-right: 5px;
}

.forgot-password {
  color: #666;
  text-decoration: none;
  font-size: 14px;
}

.forgot-password:hover {
  color: #ff5a5f;
}

.login-button {
  width: 100%;
  padding: 12px;
  background-color: #ff5a5f;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.login-button:hover {
  background-color: #ff3c41;
}

.login-button:disabled {
  background-color: #ffb6b9;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.login-footer {
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.register-link {
  color: #ff5a5f;
  text-decoration: none;
  font-weight: 500;
}

.register-link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .login-container {
    margin-left: 0;
    padding: 20px;
  }
  
  .login-box {
    padding: 30px 20px;
  }
}
</style> 